import { useState, useEffect, useCallback } from 'react';
import { fetchMySurveys } from '../api/survey.api';

/**
 * Hook pour gérer la logique métier des sondages
 * @returns {Object} - { surveys, isLoading, error, refetch, computeProgress, getStatusConfig }
 */
export function useSurveys() {
  const [surveys, setSurveys] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadSurveys = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchMySurveys();
      setSurveys(Array.isArray(data) ? data : data.data || []);
    } catch (err) {
      setError(err.message || 'Erreur lors du chargement des sondages');
      console.error('Erreur loadSurveys:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSurveys();
  }, [loadSurveys]);

  /**
   * Calcule le pourcentage de progression d'un sondage
   * @param {number} responses - Nombre de réponses
   * @param {number} target - Nombre cible de réponses
   * @returns {number} - Pourcentage (0-100)
   */
  const computeProgress = useCallback((responses, target) => {
    if (!target || target <= 0) return 0;
    return Math.max(0, Math.min(100, Math.round((responses / target) * 100)));
  }, []);

  /**
   * Retourne la configuration de statut pour un sondage
   * @param {string} status - Statut du sondage
   * @returns {Object} - { label, className }
   */
  const getStatusConfig = useCallback((status) => {
    const config = {
      Actif: { 
        label: "Actif", 
        className: "bg-success/10 text-success border-success/20" 
      },
      Inactif: { 
        label: "Brouillon", 
        className: "bg-muted text-muted-foreground border-muted" 
      },
      Clôturé: { 
        label: "Clôturé", 
        className: "bg-accent/10 text-accent border-accent/20" 
      },
    };
    return config[status] || { 
      label: status, 
      className: "bg-gray-100 text-gray-700 border-gray-300" 
    };
  }, []);

  /**
   * Extrait les données de réponses d'un sondage
   * @param {Object} survey - Objet sondage
   * @returns {Object} - { responses, target }
   */
  const extractResponseData = useCallback((survey) => {
    const responses = survey.responses_count ?? survey.reponses ?? 0;
    const target = survey.target_responses ?? survey.expected_responses ?? survey.limit_responses ?? survey.max_responses ?? 200;
    return { responses, target };
  }, []);

  return {
    surveys,
    isLoading,
    error,
    refetch: loadSurveys,
    computeProgress,
    getStatusConfig,
    extractResponseData,
  };
}
